import { create } from 'zustand';
import apiService from '../services/apiService';

const useTaskStore = create((set, get) => ({
  tasks: [],
  stats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  filters: {
    startDate: null,
    endDate: null,
    type: 'all',
    status: null,
    priority: null
  },

  // Actions
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setFilters: (filters) => set({ filters: { ...get().filters, ...filters } }),
  
  fetchTasks: async (customFilters = {}) => {
    set({ loading: true, error: null });
    
    try {
      const { filters, pagination } = get();
      
      const queryParams = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...filters,
        ...customFilters
      };

      // Remove null/undefined values
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key] || queryParams[key] === 'null') {
          delete queryParams[key];
        }
      });

      const response = await apiService.get('/tasks', { params: queryParams });

      set({
        tasks: response.data.data.tasks,
        pagination: response.data.data.pagination,
        loading: false
      });
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch tasks',
        loading: false
      });
    }
  },

  fetchTaskById: async (taskId) => {
    set({ loading: true, error: null });
    
    try {
      const response = await apiService.get(`/tasks/${taskId}`);
      return response.data.data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch task',
        loading: false
      });
      throw error;
    }
  },

  updateTaskStatus: async (taskId, status, notes = '') => {
    set({ loading: true, error: null });
    
    try {
      const response = await apiService.patch(
        `/tasks/${taskId}/status`,
        { status, notes }
      );

      // Update the task in the local state
      const { tasks } = get();
      const updatedTasks = tasks.map(task =>
        task.id === taskId
          ? { ...task, status, notes, updatedAt: new Date().toISOString() }
          : task
      );

      set({
        tasks: updatedTasks,
        loading: false
      });

      return response.data.data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to update task status',
        loading: false
      });
      throw error;
    }
  },

  updateTaskPriority: async (taskId, priority) => {
    set({ loading: true, error: null });
    
    try {
      const response = await apiService.patch(
        `/tasks/${taskId}/priority`,
        { priority }
      );

      // Update the task in the local state
      const { tasks } = get();
      const updatedTasks = tasks.map(task =>
        task.id === taskId
          ? { ...task, priority, updatedAt: new Date().toISOString() }
          : task
      );

      set({
        tasks: updatedTasks,
        loading: false
      });

      return response.data.data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to update task priority',
        loading: false
      });
      throw error;
    }
  },

  fetchTaskStats: async () => {
    try {
      const response = await apiService.get('/tasks/stats');
      set({ stats: response.data.data });
      return response.data.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch task stats' });
      throw error;
    }
  },

  setPage: (page) => {
    set({ pagination: { ...get().pagination, page } });
    get().fetchTasks();
  },

  setLimit: (limit) => {
    set({ pagination: { ...get().pagination, limit, page: 1 } });
    get().fetchTasks();
  },

  applyFilters: (newFilters) => {
    set({ 
      filters: { ...get().filters, ...newFilters },
      pagination: { ...get().pagination, page: 1 }
    });
    get().fetchTasks();
  },

  clearFilters: () => {
    set({
      filters: {
        startDate: null,
        endDate: null,
        type: 'all',
        status: null,
        priority: null
      },
      pagination: { ...get().pagination, page: 1 }
    });
    get().fetchTasks();
  }
}));

export default useTaskStore;
